import React, { useEffect, useRef, useState } from "react";
import { Form, FormControlProps, InputGroup } from "react-bootstrap";
import CommonIcons from "utils/CommonIcons";

interface AutocompleteProps {
  cansubmitCustom?: boolean;
  title?: string;
  controlId: string;
  isDescription?: boolean;
  isSOWOpen?: boolean;
  isPCROpen?: boolean;
  data: any[];
  value?: string;
  error?: string;
  isOpen?: boolean;
  suggestionClassName?: string;
  expandable?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  asterisk?: boolean;
  loadSuggestions?: boolean;
  onHideSuggestion?: boolean;
  renderSuggestionItem: (item: any, index: number) => JSX.Element;
  clearSuggestions?: () => void;
  onSuggestionClick?: (item: any, index?: number) => void;
  onChange?: FormControlProps["onChange"];
}

const Autocomplete = ({
  controlId,
  isDescription,
  title,
  data,
  value,
  error,
  isSOWOpen,
  isPCROpen,
  isOpen,
  suggestionClassName,
  cansubmitCustom,
  expandable,
  disabled,
  asterisk,
  isLoading,
  loadSuggestions,
  clearSuggestions,
  onHideSuggestion,
  onSuggestionClick,
  renderSuggestionItem,
  onChange,
}: AutocompleteProps) => {
  const [currIndex, setCurrIndex] = useState(-1);

  const typeaheadRef = useRef<any>();
  const noResult =
    !isLoading && data.length <= 0 && value?.trim() != "" && loadSuggestions;
  const suggestionsId = `${controlId}-autocomplete`;
  const errorId = error ? `${controlId}-error` : undefined;
  const isSuggestionListOpen =
    !onHideSuggestion && (isLoading || (!noResult && data.length > 0));

  useEffect(() => {
    if (currIndex != -1) {
      window?.scrollToViewAutocomplete(`#${controlId}-${currIndex + 1}`);
    }
  }, [currIndex]);

  const onItemClick = (item: any, index?: number) => {
    if (typeaheadRef?.current) typeaheadRef.current.blur();

    if (onSuggestionClick) onSuggestionClick(item, index);
  };

  const renderSuggestions = () => {
    if (isLoading) {
      return (
        <div
          role="option"
          className="border-bottom px-3 py-2 bg-white"
          tabIndex={-1}
        >
          <div className="d-flex align-items-center">
            <strong>Loading...</strong>
            <div
              className="spinner-border ms-auto spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></div>
          </div>
        </div>
      );
    }
    if (noResult) {
      if (cansubmitCustom) return;
      return (
        <div
          role="option"
          className="border-bottom px-3 py-2 bg-white fw-bold"
          tabIndex={-1}
        >
          <span>
            {cansubmitCustom ? "Create New Trade" : "No results found."}
          </span>
        </div>
      );
    }
    return data.map((suggestion, index) => {
      return (
        <div
          className={`item-option ${currIndex == index ? "active" : ""}`}
          key={`${controlId}-${index + 1}`}
          onClick={() => {
            onItemClick(suggestion, index);
          }}
          id={`${controlId}-${index + 1}`}
          role="option"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key == " " || e.key == "Enter") {
              // onItemClick(suggestion, index);
              e.currentTarget.click();
              e.preventDefault();
            }
          }}
        >
          {renderSuggestionItem(suggestion, index)}
        </div>
      );
    });
  };

  return (
    <Form.Group className="col-12 mb-4 position-relative" controlId={controlId}>
      {title && (
        <>
          <Form.Label className="fw-bold text-capitalize">
            {title}
            <span className="font-color-red">{asterisk ? "* " : ""}</span>
          </Form.Label>
          {isDescription && (
            <p>
              Address will auto populate or select arrow at end of entry field
              to enter all details manually.
            </p>
          )}
        </>
      )}
      <InputGroup>
        <Form.Control
          ref={typeaheadRef}
          id={controlId}
          type="text"
          className={`${!expandable ? "no-toggle" : ""}`}
          value={value ?? ""}
          onChange={onChange}
          disabled={disabled}
          isInvalid={error != undefined}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={suggestionsId}
          aria-describedby={errorId}
          aria-expanded={isSuggestionListOpen}
          aria-haspopup="listbox"
          aria-activedescendant={
            currIndex !== -1 ? `${controlId}-${currIndex + 1}` : undefined
          }
          onBlur={() => {
            setTimeout(() => {
              setCurrIndex(-1);
              if (clearSuggestions) clearSuggestions();
            }, 300);
          }}
          placeholder={
            isOpen || isSOWOpen || isPCROpen
              ? "Start entering address to auto-fill"
              : ""
          }
          autoComplete="off"
          onKeyDown={(e) => {
            // handle up down arrow events
            if (e.key == "Escape") {
              setCurrIndex(-1);
              if (clearSuggestions) clearSuggestions();
            }
            if (e.key == "Enter") {
              if (currIndex != -1) {
                e.preventDefault();
                onItemClick(data[currIndex], currIndex);
              }
            }
            if (e.key == "ArrowDown") {
              e.preventDefault();
              if (currIndex + 1 < data.length) {
                setCurrIndex(currIndex + 1);
              } else {
                if (data.length != 0) setCurrIndex(0);
              }
            }

            if (e.key == "ArrowUp") {
              e.preventDefault();
              if (currIndex - 1 > -1) {
                setCurrIndex(currIndex - 1);
              } else {
                if (data.length != 0) setCurrIndex(data.length - 1);
              }
            }
          }}
        />
        {expandable && (
          <button
            type="button"
            id="autocomplete-toggle"
            className="input-group-text border-start-0 bg-white"
            aria-label="Toggle manual address fields"
            aria-controls="expanded-autocomplete"
            disabled={disabled}
          >
            {isOpen || isSOWOpen || isPCROpen ? (
              <CommonIcons.ArrowDown className="autocomplete-btn" aria-hidden="true" />
            ) : (
              <CommonIcons.ArrowRight className="autocomplete-btn" aria-hidden="true" />
            )}
          </button>
        )}
        <Form.Control.Feedback
          id={errorId}
          className="font-color-red poppins"
          type="invalid"
        >
          {error}
        </Form.Control.Feedback>
      </InputGroup>
      <div
        className={`autocomplete-items text-start ${
          (suggestionClassName ?? "") +
          (onHideSuggestion ? "d-none " : "") +
          (error == undefined && isLoading && noResult && data.length
            ? "border-bottom"
            : "")
        }`}
        id={suggestionsId}
        role="listbox"
      >
        {renderSuggestions()}
      </div>
    </Form.Group>
  );
};

export default Autocomplete;
